import { Box, Chip, IconButton, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNew from '@material-ui/icons/OpenInNew';
import moment from 'moment-timezone';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import titleize from 'titleize';
import { ILink } from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './styles';

interface IProps {
  links: ILink[] | undefined;
  totalCount: number;
  onRequestFetch: (page: number, rowsPerPage: number) => void;
  onClickRow: (link: ILink) => void;
  onClickDeleteButton: (link: ILink) => void;
}

const LinkList = (props: IProps): JSX.Element => {
  const classes = useStyles();
  const { links, totalCount } = props;

  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [page, setPage] = useState<number>(0);

  const handleOpenUrlClick = useCallback((url: string) => {
    return (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      window.open(url, '_blank', 'noopener noreferrer');
    };
  }, []);

  if (links && links.length === 0) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.noDataBox} flexDirection="column">
          <Typography>No data</Typography>
          <Typography variant="caption">{'Tap the "Create a link" at the top to create a first link.'}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell align="center">ID</TableCell>
            <TableCell align="center">Link Type</TableCell>
            <TableCell align="center">Link Title</TableCell>
            <TableCell align="center">Live</TableCell>
            <TableCell align="center">Created at</TableCell>
            <TableCell align="center" size="small" padding="none">
              Delete
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {links &&
            links.map(link => {
              return (
                <TableRow
                  component={Link}
                  to={`${RouteNames.Link}/v/${link.id}`}
                  key={link.id}
                  hover
                  className={classes.tableRow}
                  onClick={() => {
                    if (!link.id) {
                      return;
                    }

                    props.onClickRow(link);
                  }}
                >
                  <TableCell align="center">{link.id}</TableCell>
                  <TableCell align="center">{titleize(link.type)}</TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <span className={classes.titleEmphasize}>{link.title}</span>
                      <button className={classes.openLink} onClick={handleOpenUrlClick(link.url)}>
                        <span className={classes.url}>
                          {link.url}
                          <OpenInNew style={{ fontSize: 16 }} color="disabled" />
                        </span>
                      </button>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label="TBD" size="small" />
                  </TableCell>
                  <TableCell align="center">{moment(link.createdAt).format('llll')}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={link.isActive ? 'You cannot delete it in the active state.' : ''} placement="left-end">
                      <IconButton
                        style={{
                          opacity: link.isActive ? 0.25 : 1,
                        }}
                        color={link.isActive ? 'default' : 'primary'}
                        disableFocusRipple={link.isActive}
                        disableTouchRipple={link.isActive}
                        disableRipple={link.isActive}
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          props.onClickDeleteButton(link);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      <TablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={(e: unknown, v: number) => {
          props.onRequestFetch(v, rowsPerPage);
          setPage(v);
        }}
        onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
          const rpp = parseInt(event.target.value, 10);
          setRowsPerPage(rpp);
          props.onRequestFetch(0, rpp);
          setPage(0);
        }}
      />
    </Paper>
  );
};

export default LinkList;
